/* eslint-disable */
import React from 'react';
import { alpha, styled } from '@mui/material/styles';

import UIKitIcon, { UIKitIconVariant } from './Icon';
import { ThemeColor } from '../theme';
import { colors } from '../theme/tokens.colors';
import { uikitPalette } from '../theme/uikitPalette';

type UIKitIconButtonSize = 'medium' | 'small';
type UIKitIconButtonVariant = 'standard' | 'outlined';
type UIKitIconButtonColor = ThemeColor | 'default';

interface UIKitIconButtonProps {
  iconName: string;
  size?: UIKitIconButtonSize;
  variant?: UIKitIconButtonVariant;
  color?: UIKitIconButtonColor;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  iconVariant?: UIKitIconVariant;
}

const StyledButton = styled('button', {
  shouldForwardProp: (prop) =>
    prop !== 'uiSize' && prop !== 'uiVariant' && prop !== 'overlayColor',
})<{ uiSize: UIKitIconButtonSize; uiVariant: UIKitIconButtonVariant; overlayColor: string }>(
  ({ uiSize, uiVariant, overlayColor }) => {
    const isSmall = uiSize === 'small';
    const box = isSmall ? 32 : 40;

    return {
      width: box,
      height: box,
      minWidth: box,
      borderRadius: 9999,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      border: uiVariant === 'outlined' ? `1px solid ${colors.neutral[300]}` : 'none',
      backgroundColor: uiVariant === 'outlined' ? colors.specialColors.white : 'transparent',
      cursor: 'pointer',
      transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',

      // 👇 Hover: overlay con alpha 8%
      '&:hover': {
        backgroundColor: alpha(overlayColor, 0.08),
        borderColor: uiVariant === 'outlined' ? colors.neutral[300] : 'transparent',
      },

      // 👇 Pressed/active: overlay con alpha 12%
      '&:active': {
        backgroundColor: alpha(overlayColor, 0.12),
        borderColor: uiVariant === 'outlined' ? colors.neutral[400] : 'transparent',
      },

      '&:disabled': {
        cursor: 'default',
        backgroundColor: 'transparent', // sin overlays
        borderColor: uiVariant === 'outlined' ? colors.neutralVariant[200] : 'transparent',
      },
    };
  }
);

const StyledIcon = styled(UIKitIcon, {
  shouldForwardProp: (prop) => prop !== 'uiSize' && prop !== 'iconColor',
})<{ uiSize: UIKitIconButtonSize; iconColor: string }>(({ uiSize, iconColor }) => ({
  fontSize: uiSize === 'small' ? 16 : 20,
  color: iconColor,
  lineHeight: 1,
}));

const UIKitIconButton: React.FC<UIKitIconButtonProps> = ({
  iconName,
  size = 'medium',
  variant = 'standard',
  color = 'primary',
  disabled,
  onClick,
  title,
  className,
  style,
  iconVariant = 'outlined',
}) => {
  let iconColor: string;
  let overlayColor: string;

  if (color === 'default') {
    iconColor = colors.neutralVariant[800];   // ícono gris oscuro
    overlayColor = colors.neutralVariant[800]; // overlay basado en el mismo gris
  } else {
    const palette = uikitPalette[color];
    iconColor = palette.main;
    overlayColor = palette.main;              // overlay de color de marca
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || !onClick) return;
    onClick(event);
  };

  return (
    <StyledButton
      type="button"
      uiSize={size}
      uiVariant={variant}
      overlayColor={overlayColor}
      className={className}
      onClick={handleClick}
      disabled={disabled}
      title={title}
      style={style}
    >
      <StyledIcon
        name={iconName}
        size={size === 'small' ? 's' : 'm'}
        uiSize={size}
        iconColor={iconColor}
        variant={iconVariant}
        style={{
          color: disabled
            ? colors.neutralVariant[300]   // Ícono grisado por disabled
            : iconColor,          // Ícono normal
        }}
      />

    </StyledButton>
  );
};

export default UIKitIconButton;
